import { useLocation } from '@remix-run/react';
import type { PAGE_POSITIONS } from 'types';
import type { Affiliate, Price } from 'types/capi/shared';
import type { TrackableProduct } from 'types/TrackableProduct';

import type { Variant } from '~/components/Button';
import { Button, ExternalLink } from '~/components/Button';
import AffiliateBuyNowEvent from '~/events/AffiliateBuyNowEvent';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import wct from '~/services/wct.client';
import type { UrlTemplates } from '~/utils/tracking';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

type Props = {
  affiliate: Affiliate;
  buttonPosition: number;
  children: React.ReactNode;
  className?: string;
  disableClickThrough?: boolean;
  pagePosition: PAGE_POSITIONS;
  price: Price;
  product: TrackableProduct;
  selectedSize?: string;
  urlTemplates: UrlTemplates;
  variant?: Variant;
};

export default function AffiliateProductLink({
  children,
  className,
  buttonPosition,
  pagePosition,
  affiliate,
  variant,
  product,
  disableClickThrough,
  selectedSize,
  price,
  urlTemplates
}: Props) {
  const region = useRegion();
  const user = useUser();
  const marketingCookies = useMarketingCookies();
  const { clientBrowser, clientIp, isBot } = useRootData();
  const { pathname, search } = useLocation();

  const landingPage = marketingCookies.getLandingPage() ?? 'unknown';

  const isSoleMarket = affiliate.id === 'affiliate:marketplace';

  const onClick = async (url: string, clickRef: string) => {
    const pagePath = new URL(pathname + search, region.baseUrl).toString();

    wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage,
      pagePath,
      product,
      url
    });

    if (!isSoleMarket)
      await new AffiliateBuyNowEvent({
        affiliateName: affiliate.name,
        buttonPosition,
        clientIp,
        currency: price.currency,
        isBot,
        isReseller: affiliate.is_reseller,
        pagePath,
        pagePosition,
        price: price.sale ?? price.regular,
        product,
        productImageUrl: product.image?.url,
        productUrl: pagePath,
        queryID: localStorage.getItem('queryID'),
        user
      }).send();
  };

  let { url, clickRef } = getTrackedUrlAndClickRef(urlTemplates, {
    buttonPosition,
    user
  });

  if (isSoleMarket && selectedSize && selectedSize !== '6')
    url += `${url.endsWith('/') ? '' : '/'}sizes/${selectedSize}/`;

  return disableClickThrough ? (
    <Button
      disabled={disableClickThrough}
      className={className}
      variant={variant ?? 'none'}
    >
      {children}
    </Button>
  ) : (
    <ExternalLink
      variant={variant ?? 'none'}
      target={isSoleMarket ? '_self' : '_blank'}
      rel={isSoleMarket ? '' : 'sponsored nofollow noopener'}
      href={url}
      onClick={() => onClick(url, clickRef)}
      className={className}
    >
      {children}
    </ExternalLink>
  );
}
